import { useState } from 'react'
import { api, downloadJournalCsv } from '../api/client'
import type { AuditKind } from '../api/types'
import { useAsync } from '../lib/useAsync'
import { journalTime, number } from '../lib/format'
import { Avatar, EmptyState, ErrorState, PageHeader, TableSkeleton } from '../components/ui'
import './Journal.css'

/**
 * Screen 7e · Admin · Fəaliyyət jurnalı.
 *
 * The journal is append-only and kept for 24 months. Nothing here can edit a
 * row — the panel offers reading, filtering and export only, which matches what
 * the database will actually permit.
 */

const RANGES = [
  { days: 1, label: 'Bugün' },
  { days: 7, label: 'Son 7 gün' },
  { days: 30, label: 'Son 30 gün' },
  { days: 365, label: 'Son 1 il' },
]

const PAGE_SIZE = 50

export function Journal() {
  const [days, setDays] = useState(7)
  const [page, setPage] = useState(0)
  const [exporting, setExporting] = useState(false)

  const { data, error, loading, reload } = useAsync(
    () => api.journal({ days, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    [days, page],
  )

  async function exportCsv() {
    setExporting(true)
    try {
      await downloadJournalCsv(days)
    } finally {
      setExporting(false)
    }
  }

  const rows = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <>
      <PageHeader
        title="Fəaliyyət jurnalı"
        subtitle="Bütün moderator qərarları · dəyişdirilə bilməz, 24 ay saxlanılır"
        actions={
          <>
            <select
              className="select journal__range"
              value={days}
              onChange={(e) => {
                setDays(Number(e.target.value))
                setPage(0)
              }}
              aria-label="Tarix aralığı"
            >
              {RANGES.map((r) => (
                <option key={r.days} value={r.days}>
                  {r.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => void exportCsv()}
              disabled={exporting}
            >
              {exporting ? 'Hazırlanır…' : 'CSV'}
            </button>
          </>
        }
      />

      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : loading ? (
        <TableSkeleton rows={8} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Bu aralıqda qeyd yoxdur"
          description="Başqa tarix aralığı seçib yenidən yoxla."
        />
      ) : (
        <>
          <div className="table-scroll">
            <div className="table journal__table">
              <div className="table__head">
                <span>Vaxt</span>
                <span>Kim · nə etdi</span>
                <span>Obyekt</span>
                <span>Növ</span>
              </div>

              {rows.map((row) => (
                <div key={row.id} className="table__row">
                  <span className="muted journal__time">{journalTime(row.createdAt)}</span>

                  <span className="row journal__actor">
                    <Avatar initials={row.actorInitials} title={`@${row.actorUsername}`} />
                    <span className="journal__action">{row.action}</span>
                  </span>

                  <span className="journal__object">{row.objectRef}</span>

                  <span>
                    <span className={`chip ${kindClass(row.kind)}`}>{row.kindLabel}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="row row--between journal__footer">
            <span className="muted">
              {number(page * PAGE_SIZE + 1)}–{number(page * PAGE_SIZE + rows.length)} /{' '}
              {number(total)} qeyd · admin qeydləri silinə bilməz
            </span>
            <div className="row">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Əvvəlki
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={rows.length < PAGE_SIZE}
                onClick={() => setPage((p) => p + 1)}
              >
                Növbəti
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}

function kindClass(kind: AuditKind): string {
  switch (kind) {
    case 'DELETE':
    case 'BLOCK':
      return 'chip--urgent'
    case 'WARN':
    case 'HIDE':
      return 'chip--medium'
    case 'REJECT':
    case 'APPEAL':
    case 'MANAGE':
      return 'chip--low'
  }
}
